import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Modal,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import React, { useContext, useEffect, useRef, useState } from "react";
import { CourseStructureContext } from "../../../../../context/CourseStructureContext";
import {
  CourseStructureCtx,
  InsightsCtx,
  InsightsFilterCtx,
  ModuleTargetFilter,
} from "../../../../../interfaces";
import { CustomCheckBox } from "../../../utils/CustomCheckBox";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import { InsightsProgressFilterContext } from "../../../../../context/InsightsProgressFilterContext";
import { OverallProgressFilterObj } from "../../../../../types";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useLocation } from "react-router-dom";
import { HandleDataLayer } from "../../../../../Helpers";
import { InsightsContext } from "../../../../../context/InsightsContext";

const useStyles = makeStyles((theme) => ({
  filterModal: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
}));

interface ProgressFilterModalProps {
  open: boolean;
  handleClose: () => void;
}

export const InsightsProgressFilterModal = (props: ProgressFilterModalProps) => {
  const { open, handleClose } = props;
  const classes = useStyles();
  const location = useLocation();
  const currentTab = Number(new URLSearchParams(location.search).get("tab")) ?? 0;

  const horiBreakpointSm = useMediaQuery("(max-width:769px)");
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const courseStructureCtxRef = useRef(courseStructureCtx);
  courseStructureCtxRef.current = courseStructureCtx;
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;

  const InsightProgressFilterContext = useContext(
    InsightsProgressFilterContext
  ) as InsightsFilterCtx;
  let appliedFilters = InsightProgressFilterContext.filters;
  const [moduleList, setModuleList] = useState<ModuleTargetFilter[]>([]);
  const [selectedModule, setSelectedModule] = React.useState<OverallProgressFilterObj>(
    appliedFilters.find((el) => el.type === "module") ?? ({} as OverallProgressFilterObj)
  );
  const [targetList, setTargetList] = React.useState<ModuleTargetFilter[]>([]);
  const [selectedTarget, setSelectedTarget] = React.useState<OverallProgressFilterObj[]>(
    appliedFilters.filter((el) => el.type === "target") ??
      ([] as OverallProgressFilterObj[])
  );

  const courseId = insightsCtx.classInfo?.course_id;

  useEffect(() => {
    if (!insightsCtx.lastClassId || !courseStructureCtxRef.current || !courseId) {
      setModuleList([]);
      return;
    }

    const moduleList =
      courseStructureCtxRef.current.getModuleFiltersInCourse(courseId) ?? [];
    setModuleList(moduleList);
  }, [insightsCtx.lastClassId, courseId]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let moduleId = (event.target as HTMLInputElement).value;
    if (!module) return;
    let moduleFilter = moduleList.find((moduleF) => moduleF.id === moduleId);
    if (!moduleFilter) return;
    HandleDataLayer(
      "select",
      "click",
      "checkbox",
      "main content",
      "modules",
      moduleFilter.name,
      "onclick",
      "filter overall progress"
    );
    setSelectedModule({ type: "module", id: moduleFilter.id, name: moduleFilter.name });
  };

  useEffect(() => {
    let appliedFilters = InsightProgressFilterContext.filters;
    setSelectedModule(
      appliedFilters.find((el) => el.type === "module") ??
        ({} as OverallProgressFilterObj)
    );
    setSelectedTarget(
      appliedFilters.filter((el) => el.type === "target") ??
        ([] as OverallProgressFilterObj[])
    );
  }, [InsightProgressFilterContext.filters]);

  const applyFilter = () => {
    if (selectedModule.id === undefined) {
      InsightProgressFilterContext.setFilters([]);
      handleClose();
      return;
    }
    InsightProgressFilterContext.setFilters([...[selectedModule], ...selectedTarget]);
    handleClose();
  };

  useEffect(() => {
    if (!courseStructureCtxRef.current || !selectedModule) return;
    if (!courseId) {
      setTargetList([]);
      return;
    }
    setTargetList(
      courseStructureCtxRef.current.getTargetFiltersInCourse(
        courseId,
        selectedModule.id
      ) ?? []
    );
    setSelectedTarget([]);
  }, [selectedModule, courseId]);
  return (
    <>
      <Modal open={open} onClose={handleClose} aria-labelledby={"progress-filter-modal"}>
        <Card
          sx={{ width: horiBreakpointSm ? "600px" : "900px" }}
          className={classes.filterModal}>
          <CardHeader
            sx={{ padding: "16px 24px" }}
            title={
              <Typography
                id="edit-account-modal-title"
                variant="h6"
                className={classes.modalTitle}>
                Filter overall progress
              </Typography>
            }
            action={
              <Tooltip
                placement="bottom-end"
                title="Close"
                arrow
                role="tooltip"
                describeChild>
                <IconButton
                  onClick={() => {
                    props.handleClose();
                    HandleDataLayer(
                      "modal",
                      "close",
                      "click",
                      "main content",
                      "filter overall progress",
                      "close cross",
                      "onclick"
                    );
                  }}
                  aria-label="close overall progress filter modal"
                  className={classes.closeButton}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent
            sx={{
              padding: "24px 32px",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}>
            <Typography
              component={"p"}
              variant="h6"
              sx={{ color: "#000000", fontWeight: "bold", fontSize: "24px" }}>
              Modules
            </Typography>
            <RadioGroup
              value={selectedModule.id ?? ""}
              onChange={handleChange}
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              sx={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)" }}>
              {moduleList?.map((module, index) => {
                return (
                  <FormControlLabel
                    key={`label-${module.id}-${index}`}
                    value={module.id}
                    control={
                      <Radio
                        key={`option-${module.id}-${index}`}
                        icon={<RadioButtonUncheckedIcon />}
                      />
                    }
                    label={module.name}
                  />
                );
              })}
            </RadioGroup>
            {currentTab !== 2 && (
              <>
                <Typography
                  component={"p"}
                  variant="h6"
                  sx={{ color: "#000000", fontWeight: "bold", fontSize: "24px" }}>
                  Learning Targets
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)" }}>
                  {targetList &&
                    targetList.length > 0 &&
                    targetList?.map((target, index) => {
                      return (
                        <CustomCheckBox
                          key={`option-${target.id}-${index}`}
                          label={target.name!}
                          checked={
                            selectedTarget.some((el) => el.id === target.id) ?? false
                          }
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            if (event.target.checked) {
                              setSelectedTarget([
                                ...selectedTarget,
                                {
                                  id: target.id!,
                                  name: target.name!,
                                  type: "target",
                                },
                              ]);
                            } else {
                              setSelectedTarget(
                                selectedTarget.filter((t) => t.id !== target.id!)
                              );
                            }
                          }}
                          emitDataLayer={(checked) => {
                            HandleDataLayer(
                              "select",
                              checked ? "click" : "unclick",
                              "checkbox",
                              "main content",
                              "learning targets",
                              target.name!,
                              "onclick",
                              "filter overall progress"
                            );
                          }}
                        />
                      );
                    })}
                  {targetList &&
                    targetList.length === 0 &&
                    moduleList &&
                    moduleList.length > 0 &&
                    targetList.map((target, index) => {
                      return (
                        <Tooltip
                          key={`placeholder-tooltip-${target.id}-${index}`}
                          title={"Pick a module first"}
                          arrow
                          placement="bottom">
                          <Box
                            component="div"
                            sx={{ display: "inline-block", marginRight: "auto" }}>
                            <CustomCheckBox
                              key={`placeholder-${target.id}-${index}`}
                              label={target.name!}
                              disabled={true}
                              checked={undefined}
                              onChange={() => {}}
                              emitDataLayer={() => {}}
                            />
                          </Box>
                        </Tooltip>
                      );
                    })}
                </Box>
              </>
            )}
          </CardContent>
          <Divider />
          <CardActions sx={{ padding: "24px 48px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}>
              <Link
                onClick={() => {
                  InsightProgressFilterContext.setFilters([]);
                  HandleDataLayer(
                    "link",
                    "click",
                    "internal link",
                    "main content",
                    "filter overall progress",
                    "clear all",
                    "onclick"
                  );
                }}
                variant="body1">
                Clear all
              </Link>
              <Link
                onClick={() => {
                  applyFilter();
                  HandleDataLayer(
                    "link",
                    "click",
                    "internal link",
                    "main content",
                    "filter overall progress",
                    "apply",
                    "onclick"
                  );
                }}
                underline="none">
                <Button
                  size="large"
                  variant="contained"
                  sx={{
                    borderRadius: "16px",
                    width: "180px",
                    height: "62px",
                    padding: "0px",
                    "&:hover": {
                      transform: "none",
                    },
                  }}>
                  Apply
                </Button>
              </Link>
            </Box>
          </CardActions>
        </Card>
      </Modal>
    </>
  );
};
